/**
 * Adapt Dynamic Package public-offer snapshot → CampaignCustomerOfferView model
 * (same shape as hydrateBelvedereCampaignCustomerOffer).
 */

import { normalizeFlightSegment } from "../adapters/buildDynamicVacationPackageViewModel";
import { formatPackageLegEndpointHe } from "../flightEndpointDisplayHe";
import { computeNightsFromSearchPhrase } from "../hotelSearchListDisplay";
import { formatIsraelDisplayDate } from "../DatesHelpers";
import {
  formatPackageLegDepartureDateHe,
  legToPremiumSummary,
} from "../premiumPackageCardModel";
import { resolveAirlineNameFromPackageLeg } from "../../Components/Common/UnifiedPackageFlightLeg/packageFlightLegAdapter";
import { buildHotelsRiverPackageQuotePdfSupplementsList } from "./mapHotelsRiverPackageQuotePdfFields";

function splitDateTime(full) {
  if (!full || typeof full !== "string") return ["", ""];
  const parts = full.trim().split(/\s+/);
  return [parts[0] || "", parts[1] || ""];
}

function toText(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return null;
}

function parsePriceIls(...values) {
  for (const value of values) {
    if (value == null || value === "") continue;
    if (typeof value === "number" && Number.isFinite(value)) {
      return Math.round(value);
    }
    const cleaned = String(value).replace(/[^\d.-]/g, "");
    const n = Number(cleaned);
    if (Number.isFinite(n) && n > 0) return Math.round(n);
  }
  return 0;
}

function pickImageSrc(value) {
  if (value == null) return null;
  if (typeof value === "string") {
    const s = value.trim();
    return s || null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const resolved = pickImageSrc(item);
      if (resolved) return resolved;
    }
    return null;
  }
  if (typeof value === "object") {
    return (
      pickImageSrc(value.FullSize) ||
      pickImageSrc(value.url) ||
      pickImageSrc(value.src) ||
      pickImageSrc(value.imageUrl) ||
      pickImageSrc(value.thumbnail)
    );
  }
  return null;
}

function collectHotelGalleryImages(selection, vm, heroImage, payload = null) {
  const hotelsItem =
    selection?.hotels_item ||
    selection?.hotelsItem ||
    null;
  const hotelName = vm?.hotel?.name || "מלון";
  const images = [];
  const push = (src, altHe, id) => {
    if (!src || images.some((row) => row.src === src)) return;
    images.push({ id: id || `img-${images.length + 1}`, src, altHe });
  };

  push(heroImage, hotelName, "hero");

  const rawSources = [
    payload?.galleryImages,
    payload?.profileGalleryImages,
    payload?.gallery_images,
    payload?.profile_gallery_images,
    vm?.galleryImages,
    vm?.gallery_images,
    vm?.heroImageUrls,
    hotelsItem?.Images,
    hotelsItem?.images,
    vm?.hotel?.images,
    vm?.hotel?.Images,
  ];

  rawSources.forEach((rawImages) => {
    if (!Array.isArray(rawImages)) return;
    rawImages.forEach((img, index) => {
      if (images.length >= 24) return;
      push(pickImageSrc(img), hotelName, `hotel-${images.length}-${index}`);
    });
  });

  return images;
}

function airportCodeFromLeg(leg, side) {
  if (!leg) return null;
  const raw = side === "dep" ? leg.depAirport : leg.arrAirport;
  if (raw && typeof raw === "object") {
    return toText(raw.code, raw.Code, raw.iata) || null;
  }
  const asText = toText(raw);
  if (asText && /^[A-Za-z]{3}$/.test(asText)) return asText.toUpperCase();
  return toText(
    side === "dep" ? leg.originCode || leg.origin : leg.destinationCode || leg.destination,
  );
}

function normalizeLegAirportsForDisplay(leg) {
  if (!leg || typeof leg !== "object") return leg;
  const next = { ...leg };
  if (typeof next.depAirport === "string" && /^[A-Za-z]{3}$/.test(next.depAirport.trim())) {
    next.depAirport = { code: next.depAirport.trim().toUpperCase() };
  }
  if (typeof next.arrAirport === "string" && /^[A-Za-z]{3}$/.test(next.arrAirport.trim())) {
    next.arrAirport = { code: next.arrAirport.trim().toUpperCase() };
  }
  return next;
}

function legToOfferRow(leg, labelHe, airlines = []) {
  if (!leg) return null;
  const displayLeg = normalizeLegAirportsForDisplay(leg);
  const [, depT] = splitDateTime(displayLeg.depDate || "");
  const [, arrT] = splitDateTime(displayLeg.arrDate || "");
  const departureTime =
    (displayLeg.depTime && String(displayLeg.depTime).trim()) || depT || "—";
  const arrivalTime =
    (displayLeg.arrTime && String(displayLeg.arrTime).trim()) || arrT || "—";
  const originCode = airportCodeFromLeg(leg, "dep");
  const destinationCode = airportCodeFromLeg(leg, "arr");
  const originLabel = formatPackageLegEndpointHe(displayLeg, "dep");
  const destinationLabel = formatPackageLegEndpointHe(displayLeg, "arr");
  const airlineName =
    toText(
      displayLeg.airlineName,
      resolveAirlineNameFromPackageLeg(displayLeg, airlines),
      displayLeg.airlineCode,
      displayLeg.Airline,
    ) || null;
  const flightNumber = toText(displayLeg.flightNumber, displayLeg.FlightNumber) || "";
  const summary = legToPremiumSummary(displayLeg);
  const depDateDisplay =
    formatPackageLegDepartureDateHe(displayLeg) ||
    summary?.dateLine ||
    null;

  return {
    id: `${labelHe}-${originCode || "x"}-${destinationCode || "y"}-${departureTime}`,
    labelHe,
    depDateDisplay,
    departureTime,
    arrivalTime,
    originCode,
    destinationCode,
    originLabel,
    destinationLabel,
    airlineName,
    flightNumber,
  };
}

function occupancyFromSearchPhrase(searchPhrase) {
  const rooms = searchPhrase?.rooms;
  if (!Array.isArray(rooms) || rooms.length === 0) return null;
  let adults = 0;
  let kids = 0;
  rooms.forEach((r) => {
    adults += Number(r.AdultsCount ?? r.adults ?? 0) || 0;
    kids += Number(r.KidsAges?.length ?? r.kids ?? 0) || 0;
  });
  const parts = [];
  if (adults > 0) parts.push(adults === 1 ? "מבוגר 1" : `${adults} מבוגרים`);
  if (kids > 0) parts.push(kids === 1 ? "ילד 1" : `${kids} ילדים`);
  if (rooms.length > 1) parts.push(`${rooms.length} חדרים`);
  return parts.length ? parts.join(" · ") : null;
}

function buildRoomDetails(selection, roomName, occupancyLabel) {
  const multi = Array.isArray(selection?.multi_room_selections)
    ? selection.multi_room_selections
    : [];
  if (multi.length > 1) {
    return multi.map((row, index) => {
      const option = row?.selected_option || row;
      const name =
        toText(
          option?.roomName,
          option?.RoomName,
          option?.selectedPackage?.RoomName,
          row?.room_line,
        ) || roomName;
      return {
        roomIndex: index + 1,
        name,
        occupancyLabel: null,
        imageUrl: null,
        packageWeightedIls: null,
      };
    });
  }

  const selectedRoom = selection?.selected_room || {};
  const name =
    toText(
      selection?.room_line_display,
      selectedRoom.roomName,
      selectedRoom.RoomName,
      selectedRoom.name,
      roomName,
    ) || roomName;

  return [
    {
      roomIndex: 1,
      name,
      occupancyLabel: occupancyLabel || null,
      imageUrl: null,
      packageWeightedIls: null,
    },
  ];
}

function baggageLinesFromVm(vm) {
  const line = toText(vm?.flights?.baggageLine);
  if (!line) return { lines: [], included: [] };
  return {
    lines: [],
    included: [line],
  };
}

/** Paid checked bags from public-offer snapshot (same mapper as PDF תוספות). */
function paidBaggageFromSnapshot(snap) {
  const raw =
    snap?.ldsSupplement ??
    snap?.lds_supplement ??
    snap?.selection?.ldsSupplement ??
    null;
  const lines = buildHotelsRiverPackageQuotePdfSupplementsList(raw);
  const extrasTotal = lines.reduce((sum, row) => {
    const n = Number(row?.priceIls);
    return sum + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);
  return {
    lines,
    extrasTotalIls: Math.round(extrasTotal),
  };
}

/**
 * @param {object} params
 * @param {object} params.payload - public offer payload (snapshot)
 * @param {object|null} params.viewModel - public_view_model
 * @param {object|null} params.branding - normalized agent branding
 * @param {string|null} params.customerName
 * @returns {object|null}
 */
export function hydrateDynamicPackageCustomerOffer({
  payload = null,
  viewModel = null,
  branding = null,
  customerName = null,
} = {}) {
  const snap = payload && typeof payload === "object" ? payload : null;
  const vm =
    (viewModel && typeof viewModel === "object" ? viewModel : null) ||
    (snap?.public_view_model && typeof snap.public_view_model === "object"
      ? snap.public_view_model
      : null);
  if (!vm || Number(vm.version) !== 1) return null;

  const selection =
    snap?.selection && typeof snap.selection === "object" ? snap.selection : {};
  const searchPhrase =
    selection.search_phrase ||
    selection.searchPhrase ||
    null;
  const airlines = Array.isArray(selection.airlines) ? selection.airlines : [];

  const depLeg = normalizeFlightSegment(selection.selected_depart);
  const retLeg = normalizeFlightSegment(selection.selected_return);

  const outbound = legToOfferRow(depLeg, "הלוך", airlines);
  const inbound = legToOfferRow(retLeg, "חזור", airlines);
  const legRows = [outbound, inbound].filter(Boolean);
  const hasFlights = legRows.length > 0;

  const checkInLabel =
    formatIsraelDisplayDate(
      toText(vm.header?.checkIn, searchPhrase?.checkIn, searchPhrase?.CheckIn)
    ) ||
    toText(vm.header?.checkIn, searchPhrase?.checkIn, searchPhrase?.CheckIn) ||
    "—";
  const checkOutLabel =
    formatIsraelDisplayDate(
      toText(
        vm.header?.checkOut,
        searchPhrase?.checkOut,
        searchPhrase?.CheckOut,
      )
    ) ||
    toText(
      vm.header?.checkOut,
      searchPhrase?.checkOut,
      searchPhrase?.CheckOut,
    ) ||
    "—";
  const nights =
    computeNightsFromSearchPhrase({
      checkIn: checkInLabel !== "—" ? checkInLabel : searchPhrase?.checkIn,
      checkOut: checkOutLabel !== "—" ? checkOutLabel : searchPhrase?.checkOut,
    }) || null;

  const hotelName = toText(vm.hotel?.name) || "מלון נבחר";
  const hotelLocation = toText(vm.hotel?.locationLine) || "";
  const boardLabel =
    toText(selection.board_label, vm.hotel?.boardLabel) || "—";
  const roomName =
    toText(selection.room_line_display, vm.room?.name) || "חדר נבחר";
  const occupancyLabel =
    toText(vm.room?.occupancyLabel) ||
    occupancyFromSearchPhrase(searchPhrase) ||
    null;

  const heroImage =
    toText(vm.heroImageUrl, vm.heroImage) ||
    pickImageSrc(snap?.galleryImages?.[0]) ||
    pickImageSrc(snap?.profileGalleryImages?.[0]) ||
    pickImageSrc(vm.galleryImages?.[0]) ||
    pickImageSrc(selection.hotels_item?.Images?.[0]) ||
    null;

  const galleryImages = collectHotelGalleryImages(
    selection,
    vm,
    heroImage,
    snap
  );
  const roomDetails = buildRoomDetails(selection, roomName, occupancyLabel);

  const bag = baggageLinesFromVm(vm);
  const paidBags = paidBaggageFromSnapshot(snap);
  const airlineSummary =
    [
      ...new Set(
        legRows.map((leg) => leg.airlineName).filter((name) => name && name !== "—"),
      ),
    ].join(", ") || null;

  const flightOrigin = outbound?.originCode || null;
  const flightDestination = outbound?.destinationCode || null;
  const routeLabelHe =
    outbound && inbound
      ? `${outbound.originLabel} → ${outbound.destinationLabel}`
      : outbound
        ? `${outbound.originLabel} → ${outbound.destinationLabel}`
        : null;

  const customerFromPayload =
    toText(
      customerName,
      snap?.customer?.name,
      snap?.customer_name,
      vm.customer_name,
    ) || null;

  const finalSalePriceIls = parsePriceIls(
    snap?.final_price_ils,
    snap?.finalPriceIls,
    vm?.price?.amountIls,
    vm?.price?.amountDisplay,
  );

  const agentBranding =
    branding && typeof branding === "object"
      ? {
          logoUrl: branding.logoUrl || null,
          agencyName: branding.agencyName || null,
          agentName: branding.agentName || null,
          phone: branding.phone || null,
          email: branding.email || null,
        }
      : null;

  return {
    orderId: null,
    kicker: toText(vm.header?.packageTaglineHe) || "חבילת נופש דינמית",
    hotelName,
    hotelLocation,
    boardLabel,
    roomName,
    roomImage: heroImage,
    roomDetails,
    heroImage,
    galleryImages,
    arrivalDate: checkInLabel !== "—" ? checkInLabel : null,
    departureDate: checkOutLabel !== "—" ? checkOutLabel : null,
    checkInLabel,
    checkOutLabel,
    nights,
    rooms: searchPhrase?.rooms || [],
    occupancyLabel,
    productType: hasFlights ? "hotel_flight" : "hotel",
    hasFlights,
    flight: hasFlights
      ? {
          legRows,
          airlineSummary,
          origin: flightOrigin,
          destination: flightDestination,
          routeLabelHe,
          includedBaggageLines: bag.included,
          includedBaggageState: bag.included.length ? "included" : "unknown",
        }
      : null,
    flightOrigin,
    passengerPricing: null,
    customerPassengerPricing: null,
    baggageLines: paidBags.lines.length ? paidBags.lines : bag.lines,
    baggageExtrasTotalIls: paidBags.extrasTotalIls,
    finalSalePriceIls,
    agentNetPriceIls: 0,
    agentAddedCommissionIls: 0,
    agentBranding,
    customerName: customerFromPayload,
    paymentPath: null,
    campaignPath: null,
  };
}
